import { r2 } from "@/lib/tool-kit/printables/page";
import {
  BASS_CLEF_PATH,
  barThickness,
  bracePath,
  lineThickness,
  type Staff,
  type System,
  staffLines,
  TREBLE_CLEF_PATH,
  TREBLE_SCALE,
} from "@/lib/tool-kit/printables/staff-paper";

const INK = "#111111";

function ClefArt({
  clef,
  x,
  top,
  space,
}: {
  clef: Staff["clef"];
  x: number;
  top: number;
  space: number;
}) {
  if (clef === "treble") {
    return (
      <g
        transform={`translate(${r2(x)} ${r2(top)}) scale(${space}) translate(0 3) scale(${TREBLE_SCALE}) translate(0 -3)`}
      >
        <path
          d={TREBLE_CLEF_PATH}
          fill="none"
          stroke={INK}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={0.21}
        />
      </g>
    );
  }
  if (clef === "bass") {
    return (
      <g transform={`translate(${r2(x)} ${r2(top)}) scale(${space})`}>
        <circle cx={0} cy={1} fill={INK} r={0.34} />
        <path
          d={BASS_CLEF_PATH}
          fill="none"
          stroke={INK}
          strokeLinecap="round"
          strokeWidth={0.28}
        />
        <circle cx={2.85} cy={0.5} fill={INK} r={0.19} />
        <circle cx={2.85} cy={1.5} fill={INK} r={0.19} />
      </g>
    );
  }
  return null;
}

/** One system: staff lines, barlines, clefs and (for a grand staff) the brace. */
export function SystemArt({
  system,
  space,
  weight,
}: {
  system: System;
  space: number;
  /** Multiplies the line and barline thickness. */
  weight: number;
}) {
  const line = r2(lineThickness(space) * weight);
  const bar = r2(barThickness(space) * weight);
  return (
    <g>
      {system.staves.map((staff) => (
        <g key={staff.top}>
          {staffLines(staff.top, space).map((y) => (
            <line
              key={y}
              stroke={INK}
              strokeWidth={line}
              x1={system.left}
              x2={system.right}
              y1={y}
              y2={y}
            />
          ))}
          <ClefArt
            clef={staff.clef}
            space={space}
            top={staff.top}
            x={system.clefX}
          />
        </g>
      ))}
      <line
        stroke={INK}
        strokeWidth={bar}
        x1={system.left}
        x2={system.left}
        y1={system.y1 - line / 2}
        y2={system.y2 + line / 2}
      />
      {system.barlines.map((x) => (
        <line
          key={x}
          stroke={INK}
          strokeWidth={bar}
          x1={x}
          x2={x}
          y1={system.y1 - line / 2}
          y2={system.y2 + line / 2}
        />
      ))}
      {system.brace ? (
        <path
          d={bracePath(
            system.brace.x,
            system.brace.y1,
            system.brace.y2,
            system.brace.width
          )}
          fill="none"
          stroke={INK}
          strokeLinecap="round"
          strokeWidth={r2(Math.max(0.5, space * 0.28))}
        />
      ) : null}
    </g>
  );
}
